import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import Drawer from "../Drawer";
import { productCatalogStatuses } from "../../consts/productCatalog";
import { getGroupsData, ProductGroupLoadingKeys } from "../../slices/productGroupSlice";
import { ProductCatalogProductsVariantsSidebar } from "./ProductCatalogProductsVariantsSidebar";
import { ProductCatalogSettingsSidebar } from "./ProductCatalogSettingsSidebar";
import { ProductCatalogDefaultsSettings } from "./ProductCatalogDefaultsSettings";

function AccordionSection({ title, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors bg-gray-50 hover:bg-gray-100"
        aria-expanded={open}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{title}</span>
          <span className="rounded border border-gray-200 px-2 py-0.5 text-xs tabular-nums text-gray-500">
            {badge}
          </span>
        </div>
        {open ? (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        ) : (
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        )}
      </button>
      {open ? (
        <div className="border-t border-gray-200 bg-gray-50/40">{children}</div>
      ) : null}
    </div>
  );
}

function StatusesInfoPanel() {
  return (
    <div className="space-y-3 px-4 py-4">
      <p className="text-sm text-gray-600">
        These lifecycle values are fixed for the catalog grid and filters. Use them when editing
        products or filtering the table.
      </p>
      <ul className="space-y-2">
        {productCatalogStatuses.map((s) => (
          <li
            key={s.value}
            className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5"
          >
            <span className="text-sm font-medium text-gray-900">{s.label}</span>
            <code className="text-xs text-gray-500">{s.value}</code>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Single Catalog Settings drawer: accordions + legacy settings blocks in one scroll (no drill-down).
 */
export function ProductCatalogCatalogHubDrawer({
  isOpen,
  onClose,
  refetchCatalogTable,
  onNavigateToSkuSearch,
  onApplyCatalogSettingFilter,
  productCatalogDefaultsSettings,
  viewPresets,
  onSaveDefaults,
  onSaveViewPresets,
  tierDiscountDrivers,
  demandTierPresets,
  onSaveTierDiscountDrivers,
}) {
  const dispatch = useDispatch();
  const { groups } = useSelector((s) => s.productGroup);
  const groupLoading = useSelector((s) =>
    (s.productGroup.loadingKeys || []).includes(ProductGroupLoadingKeys.getGroupData)
  );

  useEffect(() => {
    if (!isOpen) return;
    dispatch(
      getGroupsData({
        search: "",
        page: 1,
        itemsPerPage: 500,
      })
    );
  }, [dispatch, isOpen]);

  const variantsInGroupsCount = useMemo(() => {
    if (!groups?.length) return 0;
    return groups.reduce((acc, g) => acc + (g.variants?.length || 0), 0);
  }, [groups]);

  const statusesCount = productCatalogStatuses.length;

  const headerContent = (
    <div className="min-w-0 flex-1 pr-2">
      <h2 className="text-xl font-bold text-gray-900">Catalog Settings</h2>
      <p className="mt-1 text-sm text-gray-500">
        Manage dropdown options, view presets, and catalog defaults.
      </p>
    </div>
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="" width="500px" headerContent={headerContent}>
      <div className="flex flex-col gap-3 px-6 py-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Dropdown option lists
        </p>

        <AccordionSection
          title="Products & Variants"
          badge={groupLoading ? "…" : variantsInGroupsCount}
        >
          <ProductCatalogProductsVariantsSidebar
            embedded
            isOpen={isOpen}
            onClose={onClose}
            refetchCatalog={refetchCatalogTable}
          />
        </AccordionSection>

        <ProductCatalogSettingsSidebar
          embedded
          isOpen={isOpen}
          onClose={onClose}
          sectionFilter={null}
          onNavigateToSkuSearch={onNavigateToSkuSearch}
          onApplyCatalogSettingFilter={onApplyCatalogSettingFilter}
        />

        {/* <AccordionSection title="Statuses" badge={statusesCount}>
          <StatusesInfoPanel />
        </AccordionSection> */}

        <div className="border border-dashed border-gray-200 rounded-md px-3 py-2.5 bg-gray-50/50 text-xs text-gray-600">
          <span className="font-medium text-gray-800">Suppliers</span> are managed in the{" "}
          <span className="font-medium text-gray-800">Vendors → Suppliers</span> section. The catalog
          loads supplier options from there automatically.
        </div>

        <ProductCatalogDefaultsSettings
          embedded
          isOpen={isOpen}
          onClose={onClose}
          defaultSettings={productCatalogDefaultsSettings}
          viewPresets={viewPresets}
          onSave={onSaveDefaults}
          onSaveViewPresets={onSaveViewPresets}
          tierDiscountDrivers={tierDiscountDrivers}
          demandTierPresets={demandTierPresets}
          onSaveTierDiscountDrivers={onSaveTierDiscountDrivers}
          sectionFilter={null}
        />
      </div>
    </Drawer>
  );
}
